// Lib
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
// Api
import {
  useCreateOrderDeliveryManualMutation,
  useCreateOrderMutation,
} from "rtkQuery/query/ordersAPI";
import { useGetLoyaltySettingsQuery } from "rtkQuery/query/loyaltyAPI";
import { useGetCoreSettingsQuery } from "rtkQuery/query/coreSettingsAPI";
import { useGetCashPaymentsSettingsQuery } from "rtkQuery/query/cashPaymentsSettingsAPI";
import { useGetSettingsQuery } from "rtkQuery/query/settingsAPI";
// Hooks
import { useNotification, useViewport } from "hooks";
// Types
import {
  CustomerProfileResponseDto,
  GetCustomersAddressResponseDto,
} from "types/customers";
import {
  UserDeliveryAddressForm,
  OrderPaymentType,
  OrderType,
} from "types/orders";
import { Coupon } from "types/coupons";
import { CompleteProductData, MenuStatuses } from "./types";
// Constants
import { ADMIN_ROUTES, rtkQueryParams } from "consts";
// Helpers
import {
  generateUniqueId,
  isUserDeliveryAddressValid,
  splitPhoneNumber,
} from "helpers/dataHelpers";
import {
  calculateSummary,
  checkIfCashPaymentsAvailable,
  checkIfCoinsExpiredError,
  convertCombosToArray,
  getDeliveryAddresses,
  getLoyaltyBonusPayload,
  handleMutateOrderItems,
} from "./helpers";
// Utils
import { errorHandler } from "utils/errorHandler";
// Icons
import { DiningIcon, RightBurgerMenuIcon } from "icons";
// Components
import { ArrowBackLink, DropDown } from "components";
import { ConfirmDialog } from "components/Modals";
import {
  CustomerInformations,
  DeliveryAddress,
  LoyaltyBonusItem,
  OrderDetails,
  OrderSummary,
  Store,
  VoucherCode,
} from "./components";
// Styled
import {
  FlexContainer,
  PageHeadingContainer,
  PageTitleContainer,
} from "styled/Box";
import { Typography } from "styled/Typography";
import { Button } from "styled/Buttons";
import { AreaWrapper, PageFormsWrapper } from "./styled";

export const OrderCreate: FC = () => {
  const navigate = useNavigate();
  const { openNotification } = useNotification();

  const { isDesktop } = useViewport();

  const { data: paymentSettingsData, error: paymentSettingsError } =
    useGetCashPaymentsSettingsQuery(null, rtkQueryParams);

  const { data: settingsData, error: settingsError } = useGetSettingsQuery(
    null,
    rtkQueryParams,
  );

  const { data: coreSettings, error: coreSettingsError } =
    useGetCoreSettingsQuery(null, rtkQueryParams);

  const { data: tiersSettings, error: tiersSettingsError } =
    useGetLoyaltySettingsQuery();

  useEffect(() => {
    if (tiersSettingsError) {
      errorHandler({ error: tiersSettingsError, openNotification });
    }
  }, [tiersSettingsError]);

  useEffect(() => {
    if (coreSettingsError) {
      errorHandler({ error: coreSettingsError, openNotification });
    }
  }, [coreSettingsError]);

  useEffect(() => {
    if (paymentSettingsError) {
      errorHandler({ error: paymentSettingsError, openNotification });
    }
  }, [paymentSettingsError]);

  useEffect(() => {
    if (settingsError) {
      errorHandler({ error: settingsError, openNotification });
    }
  }, [settingsError]);

  const [createDelivery] = useCreateOrderDeliveryManualMutation();

  const [createOrder] = useCreateOrderMutation();

  const [isLoading, setIsLoading] = useState(false);

  const [isOrderCreateConfirm, setIsOrderCreateConfirm] = useState(false);

  const [locationId, setLocationId] = useState<string>("");
  const [menuId, setMenuId] = useState<string>("");
  const [menuStatuses, setMenuStatuses] = useState<MenuStatuses>({
    isClosed: false,
    isBusy: false,
    isMenuCashPaymentAvailable: false,
  });

  const [customerData, setCustomerData] =
    useState<CustomerProfileResponseDto | null>(null);

  const [isUseUserPhone, setIsUserPhone] = useState(false);

  const [deliveryAddresses, setDeliveryAddresses] = useState<
    GetCustomersAddressResponseDto[] | null
  >(null);

  const [userDeliveryAddress, setUserDeliveryAddress] =
    useState<UserDeliveryAddressForm | null>(null);

  const [orderItems, setOrderItems] = useState<CompleteProductData[]>([]);

  const [coupons, setCoupons] = useState<Coupon[]>([]);

  const [isCutlery, setIsCutlery] = useState(false);

  const [paymentType, setPaymentType] = useState<OrderPaymentType>(
    OrderPaymentType.FmcCents,
  );

  useEffect(() => {
    setUserDeliveryAddress(null);
    setLocationId(null);
    setOrderItems([]);
    setCoupons([]);
    setIsCutlery(false);
    setPaymentType(OrderPaymentType.FmcCents);
  }, [customerData]);

  const onLocationChange = useCallback((id: string) => {
    setLocationId(id);
    setPaymentType(OrderPaymentType.FmcCents);
    setOrderItems([]);
  }, []);

  const onSetIsUserPhone = useCallback(
    (value: boolean) => {
      if (value) {
        const data = splitPhoneNumber(customerData.phoneNumber);
        setUserDeliveryAddress(prev => ({
          ...prev,
          phone: data?.phone,
          phoneCode: data?.phoneCode,
        }));
      } else {
        setUserDeliveryAddress(prev => ({ ...prev, phone: "", phoneCode: "" }));
      }

      setIsUserPhone(value);
    },
    [customerData],
  );

  const handleAddOrderItem = useCallback((data: CompleteProductData) => {
    if (!data?.uniqId) {
      setOrderItems(prev =>
        prev.concat({ ...data, uniqId: generateUniqueId() }),
      );

      openNotification({ message: `${data.product.name} successfully added` });
      return;
    }

    setOrderItems(prev => handleMutateOrderItems(prev, data));

    openNotification({ message: `${data.product.name} successfully updated` });
  }, []);

  const handleRemoveOrderItem = useCallback((id: string) => {
    setOrderItems(prev => prev.filter(i => i.uniqId !== id));
  }, []);

  const handleAddCoupon = useCallback((coupon: Coupon) => {
    setCoupons(prev => {
      if (prev.find(c => c.id === coupon.id)) {
        return prev;
      }

      return prev.concat(coupon);
    });
  }, []);

  const handleRemoveCoupon = useCallback((id: string) => {
    setCoupons(prev => prev.filter(i => i.id !== id));
  }, []);

  const loyaltyDiscountAvailable = useMemo(
    () =>
      tiersSettings?.isLoyaltyFeatureEnabled &&
      !!customerData?.loyaltyData?.bonus,
    [tiersSettings, customerData],
  );

  const loyaltyData = useMemo(
    () => (loyaltyDiscountAvailable ? customerData?.loyaltyData : null),
    [loyaltyDiscountAvailable, customerData],
  );

  const summary = useMemo(
    () =>
      calculateSummary({
        coupons,
        orderItems,
        loyaltyData,
        settingsData,
        paymentSettingsData,
        customerData,
        coreSettings,
      }),
    [
      coupons,
      orderItems,
      loyaltyData,
      paymentSettingsData,
      customerData,
      settingsData,
      coreSettings,
    ],
  );

  const handlePlaceOrder = useCallback(() => {
    setIsOrderCreateConfirm(true);
  }, []);

  const handleCreateOrder = async () => {
    try {
      setIsLoading(true);
      const { street, area, block, building, city } = userDeliveryAddress;

      const { id } = await createDelivery({
        customerId: customerData.id,
        locationId,
        deliveryProvider: "deliverect",
        address: {
          street,
          area,
          block,
          building,
          city,
          phoneNumber: `+${userDeliveryAddress.phoneCode}${userDeliveryAddress.phone}`,
        },
      }).unwrap();

      const items = orderItems.map(i => ({
        productId: i.product.id,
        quantity: i.quantity,
        fmcCentsPrice: i.product.fmcCentsPrice,
        remark: i?.remark || "",
        subItems: [
          ...(convertCombosToArray(i.combos) || []).concat(
            [...i.addonsModifiers.addon, ...i.addonsModifiers.modifier].map(
              a => ({
                productId: a.id,
                quantity: a.quantity || 0,
                fmcCentsPrice: a.fmcCentsPrice,
                fiatCentsPrice: a.fiatCentsPrice,
              }),
            ),
          ),
        ],
      }));

      const order = await createOrder({
        customerId: customerData.id,
        menuId,
        items,
        totalFmcCentsPrice: summary.subTotal.coins,
        totalFiatCentsPrice: summary.subTotal.fiat,
        deliveryId: id,
        cutlery: isCutlery,
        deliveryFeeFmcCentsPrice:
          paymentType === OrderPaymentType.FmcCents
            ? Number(summary.deliveryFee.coins) || 0
            : 0,
        deliveryFeeFiatCentsPrice:
          paymentType === OrderPaymentType.FiatCents
            ? Number(summary.deliveryFee.fiat) || 0
            : 0,
        paymentType: paymentType,
        type: OrderType.INSTANT_ORDER,
        couponIds: coupons.map(c => c.id),
        loyaltyDiscount: summary.loyaltyDiscount.coins,
        loyaltyBonus: getLoyaltyBonusPayload(customerData),
        totalFiatCentsPriceOriginal: Number(summary.originalTotal.fiat) || 0,
        totalFmcCentsPriceOriginal: Number(summary.originalTotal.coins) || 0,
      }).unwrap();

      openNotification({ message: "Order successfully placed" });

      setIsOrderCreateConfirm(false);

      navigate(`${ADMIN_ROUTES.ORDERS.path}/${order.id}`);
    } catch (error) {
      errorHandler({ error, openNotification });
    } finally {
      setIsLoading(false);
    }
  };

  const { defaultDeliveryAddress, deliveryAddressesList } = useMemo(
    () => getDeliveryAddresses(deliveryAddresses),
    [deliveryAddresses],
  );

  const isOrderAvailable = useMemo(
    () => locationId && isUserDeliveryAddressValid(userDeliveryAddress),
    [locationId, userDeliveryAddress],
  );

  const isBalanceError = useMemo(
    () =>
      paymentType === OrderPaymentType.FmcCents &&
      summary.total.coins > customerData?.balanceData?.fmcCentsAmount,
    [paymentType, summary, customerData],
  );

  const minimumOrderFmcValueError = useMemo(
    () =>
      paymentType === OrderPaymentType.FmcCents &&
      summary.originalTotal.coins < settingsData?.minimumOrderValue,
    [paymentType, summary, settingsData],
  );

  const minimumOrderFiatValueError = useMemo(
    () =>
      paymentType === OrderPaymentType.FiatCents &&
      summary.originalTotal.fiat < paymentSettingsData?.minimumOrder,
    [paymentType, summary, paymentSettingsData],
  );

  const isMinimumOrderValueError = useMemo(
    () => minimumOrderFmcValueError || minimumOrderFiatValueError,
    [minimumOrderFmcValueError, minimumOrderFiatValueError],
  );

  const isExpiredCoinsError = useMemo(
    () => checkIfCoinsExpiredError(paymentType, settingsData, customerData),
    [paymentType, settingsData, customerData],
  );

  const isCashPaymentAvailable = useMemo(
    () =>
      checkIfCashPaymentsAvailable(
        locationId,
        paymentSettingsData,
        menuStatuses,
      ),
    [locationId, paymentSettingsData, menuStatuses],
  );

  const isOrderCreatingAvailable =
    !!orderItems?.length &&
    !isBalanceError &&
    !isExpiredCoinsError &&
    !isMinimumOrderValueError;

  const DropDownMenuItems = [
    {
      key: "1",
      label: "Discard",
      onClick: () => setCustomerData(null),
    },
    {
      key: "2",
      label: "Place Order",
      disabled: !isOrderCreatingAvailable,
      onClick: handlePlaceOrder,
    },
  ];

  return (
    <>
      <PageHeadingContainer $margin="0 0 24px 0">
        <PageTitleContainer $column>
          <ArrowBackLink
            title="Back to Orders"
            path={ADMIN_ROUTES.ORDERS.path}
          />

          <FlexContainer
            $gap={12}
            $align="center"
            $justify="center"
            $margin="20px 0 0 0"
          >
            <Typography.H1>Add New Order</Typography.H1>
          </FlexContainer>
        </PageTitleContainer>

        {isDesktop ? (
          <FlexContainer $gap={8}>
            <Button.Heading onClick={() => setCustomerData(null)}>
              Discard
            </Button.Heading>

            <Button.Heading
              type="primary"
              disabled={!isOrderCreatingAvailable}
              onClick={handlePlaceOrder}
            >
              Place Order
            </Button.Heading>
          </FlexContainer>
        ) : (
          <DropDown items={DropDownMenuItems} trigger={["click"]}>
            <Button.SquaredIcon icon={<RightBurgerMenuIcon />} />
          </DropDown>
        )}
      </PageHeadingContainer>

      <PageFormsWrapper>
        <FlexContainer $column $gap={16}>
          <CustomerInformations
            customerData={customerData}
            loyaltyData={loyaltyData}
            defaultDeliveryAddress={defaultDeliveryAddress}
            setCustomerData={setCustomerData}
            setDeliveryAddresses={setDeliveryAddresses}
          />

          <AreaWrapper $available="tablet">
            <Store
              isCustomerSelected={!!customerData}
              locationId={locationId}
              onLocationChange={onLocationChange}
            />

            <DeliveryAddress
              isCustomerSelected={!!customerData}
              deliveryAddressesList={deliveryAddressesList}
              isUseUserPhone={isUseUserPhone}
              userDeliveryAddress={userDeliveryAddress}
              onSetIsUserPhone={onSetIsUserPhone}
              setUserDeliveryAddress={setUserDeliveryAddress}
            />
          </AreaWrapper>

          <OrderDetails
            isBlocked={!isOrderAvailable}
            menuStatuses={menuStatuses}
            customerId={customerData?.id}
            locationId={locationId}
            orderItems={orderItems}
            isCutlery={isCutlery}
            setMenuStatuses={setMenuStatuses}
            setMenuId={setMenuId}
            handleAddOrderItem={handleAddOrderItem}
            handleRemoveOrderItem={handleRemoveOrderItem}
            setIsCutlery={setIsCutlery}
          />

          <LoyaltyBonusItem
            isBlocked={!isOrderAvailable}
            loyaltyDiscountAvailable={loyaltyDiscountAvailable}
            customerData={customerData}
            menuStatuses={menuStatuses}
          />

          <VoucherCode
            isBlocked={!isOrderAvailable}
            menuStatuses={menuStatuses}
            customerId={customerData?.id}
            coupons={coupons}
            summary={summary}
            handleAddCoupon={handleAddCoupon}
            handleRemoveCoupon={handleRemoveCoupon}
          />

          <OrderSummary
            isBlocked={!isOrderAvailable}
            menuStatuses={menuStatuses}
            isBalanceError={isBalanceError}
            isExpiredCoinsError={isExpiredCoinsError}
            isCashPayment={isCashPaymentAvailable}
            paymentType={paymentType}
            summary={summary}
            customerCoinsBalance={customerData?.balanceData?.fmcCentsAmount}
            customerCoinsExpireDate={customerData?.balanceData?.expiresAt}
            setPaymentType={setPaymentType}
          />
        </FlexContainer>

        <AreaWrapper $available="laptop">
          <Store
            isCustomerSelected={!!customerData}
            locationId={locationId}
            onLocationChange={onLocationChange}
          />

          <DeliveryAddress
            isCustomerSelected={!!customerData}
            deliveryAddressesList={deliveryAddressesList}
            isUseUserPhone={isUseUserPhone}
            userDeliveryAddress={userDeliveryAddress}
            onSetIsUserPhone={onSetIsUserPhone}
            setUserDeliveryAddress={setUserDeliveryAddress}
          />
        </AreaWrapper>
      </PageFormsWrapper>

      <ConfirmDialog
        isLoading={isLoading}
        open={isOrderCreateConfirm}
        Icon={DiningIcon}
        message="Place Order"
        description="Are you sure to continue this action?"
        onCancel={() => setIsOrderCreateConfirm(false)}
        firstCTAButton={{
          title: "Confirm",
          disabled: false,
          type: "primary",
          loading: isLoading,
          onClick: handleCreateOrder,
        }}
      />
    </>
  );
};
